import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { AlertCircle, CheckCircle2, Info, AlertTriangle, HelpCircle } from 'lucide-react';
import Button from '../Button';
import Typography from '../Typography';

const cleanDataId = (label: string) => {
  return label
    .toLowerCase()
    // Strip invisible/zero-width/control characters
    .replace(/[\u200B-\u200D\uFEFF\u200E\u200F\u202A-\u202E]/g, '')
    // Replace Polish accented characters
    .replace(/ą/g, 'a')
    .replace(/ć/g, 'c')
    .replace(/ę/g, 'e')
    .replace(/ł/g, 'l')
    .replace(/ń/g, 'n')
    .replace(/ó/g, 'o')
    .replace(/ś/g, 's')
    .replace(/ź/g, 'z')
    .replace(/ż/g, 'z')
    // Replace spaces with hyphens
    .replace(/\s+/g, '-')
    // Strip anything that is not a-z, 0-9 or hyphen
    .replace(/[^a-z0-9-]/g, '');
};

export interface PopupButton {
  label: string;
  onClick?: () => void;
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost';
}

export interface PopupProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  message: React.ReactNode;
  type?: 'info' | 'success' | 'warning' | 'error' | 'confirm';
  buttons?: PopupButton[];
  closeOnOverlayClick?: boolean;
}

export default function Popup({
  isOpen,
  onClose,
  title,
  message,
  type = 'info',
  buttons,
  closeOnOverlayClick = true
}: PopupProps) {
  // Close on ESC key press
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Icon mapping based on popup type
  const iconConfig = {
    info: {
      Icon: Info,
      color: '#3b82f6',
      bg: 'rgba(59, 130, 246, 0.08)',
      border: 'rgba(59, 130, 246, 0.15)'
    },
    success: {
      Icon: CheckCircle2,
      color: '#10b981',
      bg: 'rgba(16, 185, 129, 0.08)',
      border: 'rgba(16, 185, 129, 0.15)'
    },
    warning: {
      Icon: AlertTriangle,
      color: '#f59e0b',
      bg: 'rgba(245, 158, 11, 0.08)',
      border: 'rgba(245, 158, 11, 0.15)'
    },
    error: {
      Icon: AlertCircle,
      color: '#ef4444',
      bg: 'rgba(239, 68, 68, 0.08)',
      border: 'rgba(239, 68, 68, 0.15)'
    },
    confirm: {
      Icon: HelpCircle,
      color: 'var(--color-riding-boot-brown, #6e4d3d)',
      bg: 'rgba(110, 77, 61, 0.08)',
      border: 'rgba(110, 77, 61, 0.15)'
    }
  };

  const { Icon, color, bg, border } = iconConfig[type];

  // Default button if none provided
  const resolvedButtons: PopupButton[] = buttons && buttons.length > 0
    ? buttons
    : [{ label: 'OK', onClick: onClose, variant: 'primary' }];

  const handleOverlayClick = () => {
    if (closeOnOverlayClick) {
      onClose();
    }
  };

  return createPortal(
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(23, 32, 42, 0.45)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        zIndex: 11000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'var(--spacing-4)',
        animation: 'fadeIn 0.2s ease-out'
      }}
      onClick={handleOverlayClick}
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={title ? "popup-title" : undefined}
        aria-describedby="popup-message"
        className="glass-panel"
        style={{
          maxWidth: '400px',
          width: '100%',
          padding: 'var(--spacing-6)',
          position: 'relative',
          background: 'var(--color-bg-app, #fcfbf9)',
          border: `1px solid ${border}`,
          boxShadow: 'var(--shadow-xl, 0 20px 25px -5px rgba(0, 0, 0, 0.1))',
          borderRadius: 'var(--radius-lg, 12px)',
          animation: 'scaleUp 0.25s cubic-bezier(0.16, 1, 0.3, 1)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Type Icon Indicator */}
        <div
          style={{
            width: '48px',
            height: '48px',
            borderRadius: '50%',
            backgroundColor: bg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 'var(--spacing-4)',
            border: `1px solid ${border}`
          }}
        >
          <Icon size={24} style={{ color }} />
        </div>

        {/* Title */}
        {title && (
          <Typography
            variant="heading-sm"
            id="popup-title"
            style={{
              marginBottom: 'var(--spacing-2)',
              color: 'var(--color-text-main, #1e293b)'
            }}
          >
            {title}
          </Typography>
        )}

        {/* Message Body */}
        <div
          id="popup-message"
          style={{
            fontSize: 'var(--font-size-sm, 0.875rem)',
            color: 'var(--color-text-main, #475569)',
            lineHeight: 1.5,
            marginBottom: 'var(--spacing-6)',
            whiteSpace: 'pre-wrap'
          }}
        >
          {message}
        </div>

        {/* Action Buttons */}
        <div
          style={{
            display: 'flex',
            gap: 'var(--spacing-3, 12px)',
            width: '100%',
            justifyContent: resolvedButtons.length === 1 ? 'center' : 'flex-end',
            flexDirection: resolvedButtons.length > 2 ? 'column' : 'row'
          }}
        >
          {resolvedButtons.map((btn, index) => (
            <Button
              key={index}
              variant={btn.variant || 'secondary'}
              size="sm"
              style={{
                flex: resolvedButtons.length === 1 ? '0 1 120px' : '1 1 0%',
                minWidth: '90px'
              }}
              data-id={`popup-button-${cleanDataId(btn.label)}`}
              onClick={() => {
                if (btn.onClick) {
                  btn.onClick();
                } else {
                  onClose();
                }
              }}
            >
              {btn.label}
            </Button>
          ))}
        </div>
      </div>
    </div>,
    document.body
  );
}
